/**
 * Profile formatting and Country-derived defaults (ORCH-413, ORCH-410 §4.3)
 *
 * Purpose: The profile form shows the Locale, Time Format and Time Zone a person
 *          actually gets, while the users row keeps ORCH-191's storage: NULL means
 *          "follow". Which fields follow which is declared in metadata, not here:
 *          a `user` Field's `default_from_fielddef_id` / `default_from_attribute`
 *          (ORCH-410 §4.3) says "my default is this attribute of the value chosen in
 *          that sibling field". Locale, Time Zone, Language and Phone's calling code
 *          all follow Country that way; Time Format ← Locale is the one built-in
 *          derivation, because its default comes from `Intl`, not master data.
 * Logic: A list field's default is the source value's attribute resolved to the
 *        target list's stored id (Locale alone falls back to `en-US`, as the server
 *        does). At save, a list value equal to its derived default is sent as ''
 *        (stored NULL, follows); anything else is sent as its id (frozen). Compare
 *        at save, don't track touches, so "change it and change it back" returns to
 *        following. A text field (Phone) can't follow: the template writes the
 *        calling code into it when it is empty or holds only a prefill (a
 *        calling code with nothing typed after it, isDerivedPrefill). It is
 *        stored as typed, except that a bare prefill is stored blank: a calling
 *        code is only saved together with a number the person entered (ORCH-410).
 * Dependencies: Intl, utils/locale (DEFAULT_LOCALE), metadata-form/types (ViewField)
 */

import type { MasterDataValue } from '../types/mdm';
import type { ViewField } from '../components/metadata-form/types';
import { DEFAULT_LOCALE } from './locale';

export const COUNTRY_FIELD = 'fd_home-country';
export const LOCALE_FIELD = 'fd_locale';
export const TIME_FORMAT_FIELD = 'fd_time-format';
export const TIME_ZONE_FIELD = 'fd_time-zone';
export const LANGUAGE_FIELD = 'fd_language';
export const PHONE_FIELD = 'fd_user-phone';
export const CALLING_CODE_ATTRIBUTE = 'fd_calling-code';

/** The MDM lists the derivations read, for the form's tenant. */
export interface ProfileFormattingLists {
  countries: MasterDataValue[];
  locales: MasterDataValue[];
  hourCycles: MasterDataValue[];
  /** The `fd_time-zone` registry (ORCH-410); optional for callers that predate it. */
  timeZones?: MasterDataValue[];
  /** The `fd_language` list (ORCH-410); optional for callers that predate it. */
  languages?: MasterDataValue[];
}

/**
 * One declared derived default (ORCH-410 §4.3): `fieldId`'s default is `attribute`
 * of the value chosen in `sourceFieldId`. `datatype` is the target's: a list
 * follows (NULL = follow), text is prefilled and stored as typed.
 */
export interface DerivedDefault {
  fieldId: string;
  sourceFieldId: string;
  attribute: string;
  datatype: string;
}

/** The derived defaults a view declares, in view order. */
export const derivedDefaults = (fields: readonly ViewField[] | undefined): DerivedDefault[] =>
  (fields ?? [])
    .filter((field) => !!field.default_from_fielddef_id)
    .map((field) => ({
      fieldId: field.fielddef_id,
      sourceFieldId: field.default_from_fielddef_id as string,
      attribute: field.default_from_attribute || field.fielddef_id,
      datatype: field.field_definition.datatype,
    }));

type ProfileValues = Record<string, unknown>;

/** The list each profile field's values come from. Country is sourced from fd_country. */
const listFor = (lists: ProfileFormattingLists, fieldId: string): MasterDataValue[] =>
  (
    ({
      [COUNTRY_FIELD]: lists.countries,
      [LOCALE_FIELD]: lists.locales,
      [TIME_FORMAT_FIELD]: lists.hourCycles,
      [TIME_ZONE_FIELD]: lists.timeZones,
      [LANGUAGE_FIELD]: lists.languages,
    }) as Record<string, MasterDataValue[] | undefined>
  )[fieldId] ?? [];

/** Entities store GLOBAL ids; a tenant override carries its GLOBAL id alongside its own. */
const matches = (option: MasterDataValue, id: unknown): boolean =>
  !!id && (option.id === id || option.global_value_id === id);

const findValue = (values: MasterDataValue[], id: unknown): MasterDataValue | undefined =>
  values.find((option) => matches(option, id));

const findByCode = (values: MasterDataValue[], code: string): MasterDataValue | undefined =>
  values.find((option) => option.meta_data?.fd_code === code);

/** The id an entity stores for a value: its GLOBAL id when it overrides one. */
const storedId = (option: MasterDataValue | undefined): string | null =>
  option ? option.global_value_id || option.id : null;

/** Value id → the matching option's stored id, so ids compare across overrides. */
const canonicalId = (values: MasterDataValue[], id: unknown): string | null =>
  storedId(findValue(values, id)) ?? (typeof id === 'string' && id ? id : null);

/**
 * A list target with no assignment on its source: Locale alone has a fallback (en-US, as the
 * server). Time Zone and Language have none: a person's language is never assumed English.
 */
const LIST_FALLBACKS: Record<string, (targets: MasterDataValue[]) => string | null> = {
  [LOCALE_FIELD]: (locales) => storedId(findByCode(locales, DEFAULT_LOCALE)),
};

/** The attribute a source value carries, first entry when stored as a list. */
const attributeOf = (source: MasterDataValue | undefined, attribute: string): unknown => {
  const raw = source?.meta_data?.[attribute];
  return Array.isArray(raw) ? raw[0] : raw;
};

/**
 * The default a declaration gives for a source value
 *
 * @returns For a list target, the target list's stored id (null when nothing
 *          resolves and the field has no fallback); for text, the attribute string.
 */
export const derivedDefault = (
  derivation: DerivedDefault,
  sourceValue: unknown,
  lists: ProfileFormattingLists
): string | null => {
  const assigned = attributeOf(
    findValue(listFor(lists, derivation.sourceFieldId), sourceValue),
    derivation.attribute
  );
  if (derivation.datatype === 'list') {
    const targets = listFor(lists, derivation.fieldId);
    return (
      storedId(findValue(targets, assigned)) ??
      LIST_FALLBACKS[derivation.fieldId]?.(targets) ??
      null
    );
  }
  return typeof assigned === 'string' && assigned.trim() ? assigned.trim() : null;
};

/**
 * Whether a text value is only a prefill: some source value's attribute, with nothing
 * typed after it (a bare calling code, whichever country's). Such a value is a default,
 * not an entry: the template replaces it when the source changes and toStored sends it
 * blank. Judged against the whole source list, not a remembered "last prefill", so it
 * holds however often the form is rebuilt.
 */
export const isDerivedPrefill = (
  derivation: DerivedDefault,
  value: unknown,
  lists: ProfileFormattingLists
): boolean => {
  const typed = typeof value === 'string' ? value.trim() : '';
  return (
    !!typed &&
    listFor(lists, derivation.sourceFieldId).some((source) => {
      const attribute = attributeOf(source, derivation.attribute);
      return typeof attribute === 'string' && attribute.trim() === typed;
    })
  );
};

const LOCALE_FROM_COUNTRY: DerivedDefault = {
  fieldId: LOCALE_FIELD,
  sourceFieldId: COUNTRY_FIELD,
  attribute: LOCALE_FIELD,
  datatype: 'list',
};

/**
 * The locale a Country assigns
 *
 * @returns The `fd_locale` value id, else the `en-US` value's id (null if neither exists)
 */
export const countryLocaleId = (
  countryId: unknown,
  countries: MasterDataValue[],
  locales: MasterDataValue[]
): string | null =>
  derivedDefault(LOCALE_FROM_COUNTRY, countryId, { countries, locales, hourCycles: [] });

/** `fd_locale` value → its BCP 47 tag (`meta_data.fd_code`), else null. */
export const localeTagOf = (localeId: unknown, locales: MasterDataValue[]): string | null => {
  const code = findValue(locales, localeId)?.meta_data?.fd_code;
  return typeof code === 'string' && code ? code : null;
};

/** `fd_time-format` value → `h12` / `h23`, else null. */
export const hourCycleCodeOf = (
  hourCycleId: unknown,
  hourCycles: MasterDataValue[]
): 'h12' | 'h23' | null => {
  const code = findValue(hourCycles, hourCycleId)?.meta_data?.fd_code;
  return code === 'h12' || code === 'h23' ? code : null;
};

/**
 * A locale's own clock, as `Intl` uses it when no hourCycle is given
 *
 * Logic: h11/h12 → h12, h23/h24 → h23 (ja-JP resolves h23, never h24).
 */
export const defaultHourCycle = (tag: string): 'h12' | 'h23' => {
  try {
    const resolved = (
      new Intl.DateTimeFormat(tag, { hour: 'numeric' }).resolvedOptions() as {
        hourCycle?: string;
      }
    ).hourCycle;
    return resolved === 'h11' || resolved === 'h12' ? 'h12' : 'h23';
  } catch {
    return 'h12';
  }
};

/** The `fd_time-format` value id for a Locale's own clock (null if not derivable). */
export const localeHourCycleId = (
  localeId: unknown,
  locales: MasterDataValue[],
  hourCycles: MasterDataValue[]
): string | null => {
  const tag = localeTagOf(localeId, locales) ?? DEFAULT_LOCALE;
  return storedId(findByCode(hourCycles, defaultHourCycle(tag)));
};

/** The declared list derivations only: text targets are prefilled, not resolved. */
const listDerivations = (derivations: DerivedDefault[]): DerivedDefault[] =>
  derivations.filter((d) => d.datatype === 'list');

const textDerivations = (derivations: DerivedDefault[]): DerivedDefault[] =>
  derivations.filter((d) => d.datatype !== 'list');

/**
 * Stored profile → form values: fill each blank following field with its derived value
 *
 * Logic: Each blank is filled independently, so a frozen Time Format under a
 *        following Locale stays as stored. Declared list derivations first (Locale,
 *        Time Zone from Country), then the built-in Time Format from the displayed Locale.
 */
export const resolveDisplayed = (
  stored: ProfileValues,
  lists: ProfileFormattingLists,
  derivations: DerivedDefault[] = [LOCALE_FROM_COUNTRY]
): ProfileValues => {
  const displayed: ProfileValues = { ...stored };
  listDerivations(derivations).forEach((d) => {
    displayed[d.fieldId] =
      stored[d.fieldId] || derivedDefault(d, stored[d.sourceFieldId], lists) || '';
  });
  displayed[TIME_FORMAT_FIELD] =
    stored[TIME_FORMAT_FIELD] ||
    localeHourCycleId(displayed[LOCALE_FIELD], lists.locales, lists.hourCycles) ||
    '';
  return displayed;
};

/**
 * Form values → PATCH body: a list value equal to its default is sent as '' (follow)
 *
 * Logic: Each declared list field is compared with what its source derives;
 *        Time Format with the displayed Locale's clock. A declared text field
 *        (Phone) holding only its prefill — the calling code and nothing typed
 *        after it — is sent as '': the code is a default, not a number anyone
 *        entered, so it is neither a change nor stored on its own (ORCH-410).
 *        Other text passes as typed.
 */
export const toStored = (
  displayed: ProfileValues,
  lists: ProfileFormattingLists,
  derivations: DerivedDefault[] = [LOCALE_FROM_COUNTRY]
): ProfileValues => {
  const stored: ProfileValues = { ...displayed };
  listDerivations(derivations).forEach((d) => {
    const value = canonicalId(listFor(lists, d.fieldId), displayed[d.fieldId]);
    const fallback = derivedDefault(d, displayed[d.sourceFieldId], lists);
    stored[d.fieldId] = !value || value === fallback ? '' : value;
  });
  textDerivations(derivations).forEach((d) => {
    if (isDerivedPrefill(d, displayed[d.fieldId], lists)) stored[d.fieldId] = '';
  });
  const locale = canonicalId(lists.locales, displayed[LOCALE_FIELD]);
  const hourCycle = canonicalId(lists.hourCycles, displayed[TIME_FORMAT_FIELD]);
  const hourCycleDefault = localeHourCycleId(locale, lists.locales, lists.hourCycles);
  stored[TIME_FORMAT_FIELD] = !hourCycle || hourCycle === hourCycleDefault ? '' : hourCycle;
  return stored;
};
